import {
  BpkBox,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import {
  BpkTable,
  BpkTableBody,
} from '@skyscanner-internal/backpack-web/bpk-component-table';

import withErrorBoundary from '../ErrorBoundary';
import FooterBlurb from '../common/FooterBlurb';
import { useMBDContext } from '../common/MBD/useMBDContext';

import Snippet from './Snippet';

import type {
  InfoSnippetDecoupledProps,
  SnippetProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/InfoSnippetDecoupledProps';

import STYLES from './InfoSnippet.module.scss';

const InfoSnippetDecoupled = ({
  disclaimer,
  header: title,
  isMobile = false,
  snippets,
  subheader,
  withItemsBackground,
}: InfoSnippetDecoupledProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!snippets?.length) {
    return null;
  }

  return (
    <div
      className={STYLES.InfoSnippet}
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <BpkSectionHeader title={title} description={subheader} />
      <BpkTable className={STYLES.Snippets}>
        <BpkTableBody
          className={
            withItemsBackground
              ? STYLES.Snippets__wrapWithBackground
              : STYLES.Snippets__wrap
          }
        >
          {snippets.map((snippet: SnippetProps, index) => (
            <Snippet
              key={snippet.label}
              {...snippet}
              isMobile={isMobile}
              // Preserve the stable, semantic snippetId set by the data
              // fetcher (e.g. 'highestRatedHotel'), only falling back to the
              // array index for callers that don't set one. Previously this
              // unconditionally overrode the fetched snippetId with the
              // array index, discarding it entirely.
              snippetId={snippet.snippetId || index}
              withItemsBackground={withItemsBackground}
            />
          ))}
        </BpkTableBody>
      </BpkTable>
      {disclaimer && (
        <BpkBox marginTop={BpkSpacing.XL}>
          <FooterBlurb disclaimerCopy={disclaimer} />
        </BpkBox>
      )}
    </div>
  );
};

const InfoSnippetDecoupledWithErrorBoundary = withErrorBoundary(
  InfoSnippetDecoupled,
  'info-snippet-decoupled-component',
);
export default InfoSnippetDecoupledWithErrorBoundary;
